import React, { useState, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { BaseLayout } from '../../';
import {
	Paper,
	Typography,
	Grid,
	TextField,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Snackbar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import CustomOwnerLetterDialog from '../../comps/Dialogs/CustomOwnerLetterDialog';
import { getLetterTemplates, getPdfOfCustomMultipleListing } from '../../../api/serverRequests';
import { useUserState } from '../../../hook/customerHook';

const useStyles = makeStyles((theme) => ({
	paper: {
		padding: theme.spacing(3),
		display: 'flex',
		overflow: 'auto',
		flexDirection: 'column',
		backgroundColor: theme.palette.background.paper,
		boxShadow: theme.shadows[2],
		borderRadius: theme.shape.borderRadius
	},
	searchContainer: {
		marginBottom: theme.spacing(4),
		display: 'flex',
		gap: theme.spacing(2),
		alignItems: 'center'
	},
	searchField: {
		flexGrow: 1
	},
	searchButton: {
		minWidth: 120
	},
	resultCard: {
		marginTop: theme.spacing(2)
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		marginTop: theme.spacing(4)
	},
	actionButtons: {
		display: 'flex',
		gap: theme.spacing(2),
		marginTop: theme.spacing(2)
	},
	generateButton: {
		backgroundColor: '#004F2B',
		color: 'white',
		'&:hover': {
			backgroundColor: '#003920'
		}
	}
}));

export default function Flyer() {
	const classes = useStyles();
	const [userState, dispatchUser] = useUserState();
	const [address, setAddress] = useState('');
	const [city, setCity] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [propertyData, setPropertyData] = useState(null);
	const [openSnackbar, setOpenSnackbar] = useState(false);

	// New states for CustomOwnerLetterDialog
	const [openLetterDialog, setOpenLetterDialog] = useState(false);
	const [letterTemplates, setLetterTemplates] = useState([]);
	const [selectedTemplate, setSelectedTemplate] = useState('');
	const [checkedLetterhead, setCheckedLetterhead] = useState(true);
	const [checkedUsername, setCheckedUsername] = useState(userState.checkedUsername);
	const [popupLoading, setPopupLoading] = useState(false);
	const [pdfCustomOwnerUrl, setPdfCustomOwnerUrl] = useState('');
	const [ownerLetterError, setOwnerLetterError] = useState(false);
	const [tutorialOpen, setTutorialOpen] = useState(false);

	// Fetch letter templates on component mount
	useEffect(() => {
		getLetterTemplates()
			.then((res) => {
				if (Array.isArray(res.data)) {
					setLetterTemplates(res.data);
					if (res.data.length > 0) {
						setSelectedTemplate(res.data[0].Id);
					}
				}
			})
			.catch((error) => {
				console.error('Error fetching letter templates:', error);
				setLetterTemplates([]);
			});
	}, []);

	const handleSearch = async () => {
		if (!address || !city) {
			setError('Please enter both address and city');
			setOpenSnackbar(true);
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const response = await fetch(
				`/api/geocode?address=${encodeURIComponent(address)}&city=${encodeURIComponent(
					city
				)}`
			);
			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message || 'Failed to fetch property data');
			}

			setPropertyData(data);
		} catch (err) {
			setError(err.message);
			setOpenSnackbar(true);
		} finally {
			setLoading(false);
		}
	};

	const handleCloseSnackbar = () => {
		setOpenSnackbar(false);
	};

	const handleGeneratePdf = async () => {
		if (!propertyData) {
			setOwnerLetterError(true);
			return;
		}

		// Log the propertyData to see its structure
		console.log('Property Data:', propertyData);

		// Adjust this based on where MLNum is in your data structure
		const mlNum = propertyData.MLNum || propertyData.mlNumber || propertyData.listingId;

		if (!mlNum) {
			setOwnerLetterError(true);
			console.error('No MLNum found in property data');
			return;
		}

		setPopupLoading(true);
		setPdfCustomOwnerUrl('');
		setOwnerLetterError(false);

		try {
			const response = await getPdfOfCustomMultipleListing(
				[mlNum],
				userState.user.name,
				'Generate PDF',
				checkedLetterhead,
				checkedUsername,
				selectedTemplate
			);

			if (response.status === 200) {
				const downloadUrl = window.URL.createObjectURL(response.data);
				setPdfCustomOwnerUrl(downloadUrl);
			}
		} catch (err) {
			setOwnerLetterError(true);
			console.error('Error generating PDF:', err);
			if (err.response.status === 401) {
				dispatchUser({
					isLoggedIn: false
				});
			}
		} finally {
			setPopupLoading(false);
		}
	};

	return (
		<BaseLayout>
			<Paper className={classes.paper}>
				<Typography variant="h5" gutterBottom>
					Property Search
				</Typography>

				<div className={classes.searchContainer}>
					<TextField
						className={classes.searchField}
						label="Address"
						variant="outlined"
						value={address}
						onChange={(e) => setAddress(e.target.value)}
						placeholder="Enter property address"
					/>
					<TextField
						className={classes.searchField}
						label="City"
						variant="outlined"
						value={city}
						onChange={(e) => setCity(e.target.value)}
						placeholder="Enter city"
					/>
					<Button
						variant="contained"
						color="primary"
						className={classes.searchButton}
						onClick={handleSearch}
						disabled={loading}
						startIcon={<SearchIcon />}
					>
						Search
					</Button>
				</div>

				{loading && (
					<div className={classes.loadingContainer}>
						<CircularProgress />
					</div>
				)}

				{propertyData && (
					<Card className={classes.resultCard}>
						<CardContent>
							<Typography variant="h6" gutterBottom>
								Property Details
							</Typography>
							<Typography>Address: {propertyData.address}</Typography>
							<Typography>City: {propertyData.city}</Typography>
							<Typography>Latitude: {propertyData.latitude}</Typography>
							<Typography>Longitude: {propertyData.longitude}</Typography>

							<div className={classes.actionButtons}>
								<Button
									variant="contained"
									className={classes.generateButton}
									startIcon={<DescriptionIcon />}
									onClick={() => setOpenLetterDialog(true)}
								>
									Generate Owner Letter
								</Button>
							</div>
						</CardContent>
					</Card>
				)}

				<CustomOwnerLetterDialog
					open={openLetterDialog}
					onClose={() => setOpenLetterDialog(false)}
					popupLoading={popupLoading}
					pdfCustomOwnerUrl={pdfCustomOwnerUrl}
					ownerLetterError={ownerLetterError}
					letterTemplates={letterTemplates}
					selectedTemplate={selectedTemplate}
					onTemplateChange={(e) => setSelectedTemplate(e.target.value)}
					checkedLetterhead={checkedLetterhead}
					onLetterheadChange={(e) => setCheckedLetterhead(e.target.checked)}
					checkedUsername={checkedUsername}
					onUsernameChange={(e) => setCheckedUsername(e.target.checked)}
					onGeneratePdf={handleGeneratePdf}
					tutorialOpen={tutorialOpen}
					onTutorialClose={setTutorialOpen}
				/>

				<Snackbar
					open={openSnackbar}
					autoHideDuration={6000}
					onClose={handleCloseSnackbar}
					message={error}
				/>
			</Paper>
		</BaseLayout>
	);
}
